import { MapNumberTo, MapStringTo, Nullable, Object_Unknown } from "../../..";
import { IContentHubClient } from "../../../clients/content-hub-client";
import DirtyValueCalculator from "../../../dirty-value-calculator";
import Guard from "../../../guard";
import { EntityLoadConfiguration } from "../../querying/entity-load-configuration";
import { IParentRelation, RelationBase } from "../relation";
import { RelationRole } from "../relation-role";
import { IToManyRelation } from "./to-many-relation";

/**
 * A relation where the parent has many children.
 */
export interface IParentToManyChildrenRelation extends IParentRelation, IToManyRelation {
    /**
     * The list of children ids.
     */
    readonly children: Array<number>;

    /**
     * When related items are partially loaded, this value will be set
     *  to indicate how many related items there are in total.
     */
    childTotal?: number;

    /**
     * Gets the properties for a child.
     *
     * If no properties are found, an empty list is returned.
     * @param id - The id of the child to get properties for
     */
    getChildProperties(id: number): MapStringTo<Object_Unknown>;
}

export class ParentToManyChildrenRelation extends RelationBase implements IParentToManyChildrenRelation {
    private readonly _children: Array<number> = [];

    readonly role: RelationRole = RelationRole.Parent;

    readonly childTotal?: number;

    readonly isMultiValue: boolean = true;

    get isFullyLoaded(): boolean {
        return this.childTotal == null || this._children.length === this.childTotal;
    }

    get isDirty(): boolean {
        if (!this.isTracking) {
            return false;
        } else {
            return this._dirtyValueCalculator.isDirty(this._children);
        }
    }

    get children(): Array<number> {
        return this._children.slice();
    }

    constructor(
        name: string,
        properties: Nullable<MapNumberTo<MapStringTo<Object_Unknown>>> = null,
        client?: IContentHubClient,
        childTotal?: number
    ) {
        super(name, properties, client);
        this.childTotal = childTotal;
    }

    getIds(): Array<number> {
        return this._children.slice();
    }

    setIds(ids: Array<number>): void {
        Guard.validIds(ids);

        this._children.splice(0, this._children.length);

        ids.forEach(id => {
            this._children.push(id);
        });
    }

    clear(): void {
        this._children.splice(0, this._children.length);
    }

    add(id: number): void {
        Guard.validId(id);
        if (!this._children.includes(id)) {
            this._children.push(id);
        }
    }

    addRange(ids: Array<number>): void {
        Guard.validIds(ids);
        ids.forEach(id => {
            if (!this._children.includes(id)) {
                this._children.push(id);
            }
        });
    }

    async addAsync(identifier: string): Promise<void> {
        Guard.notNullOrUndefined(this.client);
        Guard.stringNotNullOrEmpty(identifier);

        const entity = await this.client!.entities.getAsync(identifier, EntityLoadConfiguration.Minimal);

        if (entity && entity.id) {
            this.add(entity.id);
        }
    }

    async addRangeAsync(identifiers: Array<string>): Promise<void> {
        Guard.notNullOrUndefined(this.client);
        Guard.arrayNoneNullOrEmptyString(identifiers);

        const entities = await this.client!.entities.getManyAsync(identifiers, EntityLoadConfiguration.Minimal);

        if (entities) {
            this.addRange(entities.filter(x => x.id !== null).map(x => x.id!));
        }
    }

    startTracking(): void {
        if (this.isTracking) return;

        this.isTracking = true;

        this._dirtyValueCalculator = new DirtyValueCalculator();
        this._dirtyValueCalculator.setOriginalValue(this._children);
    }

    markClean(): void {
        if (this.isTracking) {
            this._dirtyValueCalculator.setOriginalValue(this._children);
        }
    }

    getChildProperties(id: number): MapStringTo<Object_Unknown> {
        return this.properties[id] == null ? {} : this.properties[id];
    }
}
